import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LLMProvider, LLMModelInfo, LLMRoleConfigMap } from '../types';
import {
  listLLMProviders,
  createLLMProvider,
  updateLLMProvider,
  toggleLLMProvider,
  deleteLLMProvider,
  getLLMRoleConfig,
  updateLLMRoleConfig,
} from '../api';
import { validateLLMExtraConfig } from '../utils/llmExtraConfig';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

interface ModelFormItem {
  sourceMetadata: Record<string, unknown>;
  name: string;
  displayName: string;
  supportThinking: boolean;
  supportVision: boolean;
  fixedParams: boolean;
  maxOutputTokens: string;
  maxContextTokens: string;
  inputPrice: string;
  outputPrice: string;
  inputCredits: string;
  outputCredits: string;
  disabled: boolean;
  useOverseasBucket: boolean;
  extraConfig: string;
}

const emptyModel = (): ModelFormItem => ({ sourceMetadata: {}, name: '', displayName: '', supportThinking: false, supportVision: false, fixedParams: false, maxOutputTokens: '', maxContextTokens: '', inputPrice: '', outputPrice: '', inputCredits: '', outputCredits: '', disabled: false, useOverseasBucket: false, extraConfig: '' });

const parseModels = (s?: string): LLMModelInfo[] => {
  try { return JSON.parse(s || '[]'); } catch { return []; }
};

const positiveInteger = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const ROLE_KEYS: { key: keyof LLMRoleConfigMap; labelKey: string; descKey: string }[] = [
  { key: 'pronounResolve', labelKey: 'llm.roles.pronounResolve', descKey: 'llm.roles.pronounResolveDesc' },
  { key: 'extract', labelKey: 'llm.roles.extract', descKey: 'llm.roles.extractDesc' },
  { key: 'videoPrompt', labelKey: 'llm.roles.videoPrompt', descKey: 'llm.roles.videoPromptDesc' },
  { key: 'assetMatch', labelKey: 'llm.roles.assetMatch', descKey: 'llm.roles.assetMatchDesc' },
  { key: 'directorMarkMapping', labelKey: 'llm.roles.directorMarkMapping', descKey: 'llm.roles.directorMarkMappingDesc' },
  { key: 'scriptNormalize', labelKey: 'llm.roles.scriptNormalize', descKey: 'llm.roles.scriptNormalizeDesc' },
  { key: 'assetKeyword', labelKey: 'llm.roles.assetKeyword', descKey: 'llm.roles.assetKeywordDesc' },
  { key: 'castingTags', labelKey: 'llm.roles.castingTags', descKey: 'llm.roles.castingTagsDesc' },
  { key: 'castingRank', labelKey: 'llm.roles.castingRank', descKey: 'llm.roles.castingRankDesc' },
  { key: 'stylingTags', labelKey: 'llm.roles.stylingTags', descKey: 'llm.roles.stylingTagsDesc' },
  { key: 'languageRewrite', labelKey: 'llm.roles.languageRewrite', descKey: 'llm.roles.languageRewriteDesc' },
  { key: 'analyticsReport', labelKey: 'llm.roles.analyticsReport', descKey: 'llm.roles.analyticsReportDesc' },
  { key: 'refTranslate', labelKey: 'llm.roles.refTranslate', descKey: 'llm.roles.refTranslateDesc' },
];

const defaultRoleConfig = (): LLMRoleConfigMap => ({
  pronounResolve: { providerId: '', model: '' },
  extract: { providerId: '', model: '' },
  videoPrompt: { providerId: '', model: '' },
  assetMatch: { providerId: '', model: '' },
  scriptNormalize: { providerId: '', model: '' },
  assetKeyword: { providerId: '', model: '' },
  castingTags: { providerId: '', model: '' },
  castingRank: { providerId: '', model: '' },
  stylingTags: { providerId: '', model: '' },
  languageRewrite: { providerId: '', model: '' },
  refTranslate: { providerId: '', model: '' },
  analyticsReport: { providerId: '', model: '' },
  directorMarkMapping: { providerId: '', model: '' },
});

const LLMConfig: React.FC = () => {
  const { t } = useTranslation('config');
  const toast = useToast();
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [roleConfig, setRoleConfig] = useState<LLMRoleConfigMap>(defaultRoleConfig);
  const [loading, setLoading] = useState(true);
  const [savingRoles, setSavingRoles] = useState(false);
  const [roleMsg, setRoleMsg] = useState('');

  // Provider modal
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LLMProvider | null>(null);
  const [providerForm, setProviderForm] = useState({ name: '', apiBase: '', apiKey: '', maxConcurrency: '' });
  const [modelList, setModelList] = useState<ModelFormItem[]>([emptyModel()]);
  const [priceUnit, setPriceUnit] = useState<'kToken' | 'MToken'>('kToken');
  const [providerSaving, setProviderSaving] = useState(false);
  const [providerError, setProviderError] = useState('');

  // Delete confirm
  const [deletingProvider, setDeletingProvider] = useState<LLMProvider | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [p, rc] = await Promise.all([listLLMProviders(), getLLMRoleConfig()]);
      setProviders(p || []);
      setRoleConfig({ ...defaultRoleConfig(), ...(rc || {}) });
    } finally {
      setLoading(false);
    }
  };

  // Provider CRUD
  // 存储标准：元/千Token。显示时根据 priceUnit 换算。
  const storedToDisplay = (v: number | undefined, unit: 'kToken' | 'MToken') => {
    if (!v) return '';
    return unit === 'MToken' ? String(v * 1000) : String(v);
  };
  const displayToStored = (v: string, unit: 'kToken' | 'MToken') => {
    const n = parseFloat(v);
    if (!n || isNaN(n)) return 0;
    return unit === 'MToken' ? n / 1000 : n;
  };

  const openCreateProvider = () => {
    setEditingProvider(null);
    setProviderForm({ name: '', apiBase: '', apiKey: '', maxConcurrency: '' });
    setModelList([emptyModel()]);
    setPriceUnit('kToken');
    setProviderError('');
    setShowProviderModal(true);
  };

  const openEditProvider = (p: LLMProvider) => {
    setEditingProvider(p);
    setProviderForm({ name: p.name, apiBase: p.apiBase, apiKey: '', maxConcurrency: p.maxConcurrency == null ? '' : String(p.maxConcurrency) });
    const unit = 'kToken' as const;
    const models = parseModels(p.models);
    setModelList(models.length > 0 ? models.map(m => ({
      sourceMetadata: { ...m },
      ...m,
      disabled: m.disabled || false,
      fixedParams: m.fixedParams || false,
      maxOutputTokens: m.maxOutputTokens ? String(m.maxOutputTokens) : '',
      maxContextTokens: m.maxContextTokens ? String(m.maxContextTokens) : '',
      inputPrice: storedToDisplay(m.inputPrice, unit),
      outputPrice: storedToDisplay(m.outputPrice, unit),
      inputCredits: m.inputCredits ? String(m.inputCredits) : '',
      outputCredits: m.outputCredits ? String(m.outputCredits) : '',
      useOverseasBucket: !!m.useOverseasBucket,
      extraConfig: m.extraConfig || '',
    })) : [emptyModel()]);
    setPriceUnit(unit);
    setProviderError('');
    setShowProviderModal(true);
  };

  const handleSaveProvider = async () => {
    if (!providerForm.name.trim()) { setProviderError(t('llm.nameEmpty')); return; }
    if (!providerForm.apiBase.trim()) { setProviderError(t('llm.apiBaseEmpty')); return; }
    if (!editingProvider && !providerForm.apiKey.trim()) { setProviderError(t('llm.apiKeyEmpty')); return; }
    const validModels = modelList.filter(m => m.name.trim());
    if (validModels.length === 0) { setProviderError(t('llm.atLeastOneModel')); return; }

    for (const m of validModels) {
      if (m.maxContextTokens.trim() && positiveInteger(m.maxContextTokens) === undefined) {
        setProviderError(t('llm.maxContextTokensInvalid', { name: m.name.trim() }));
        return;
      }
      const errCode = validateLLMExtraConfig(m.extraConfig);
      if (errCode) {
        setProviderError(t('llm.extraConfigInvalid', { name: m.name.trim() }) + t(`llm.extraConfigErr.${errCode}`));
        return;
      }
    }

    setProviderSaving(true);
    setProviderError('');
    try {
      const modelsJson = JSON.stringify(validModels.map(m => ({
        ...m.sourceMetadata,
        name: m.name.trim(),
        displayName: m.displayName.trim() || m.name.trim(),
        supportThinking: m.supportThinking,
        supportVision: m.supportVision,
        useOverseasBucket: m.useOverseasBucket,
        fixedParams: m.fixedParams,
        maxOutputTokens: m.maxOutputTokens ? parseInt(m.maxOutputTokens, 10) || 0 : undefined,
        maxContextTokens: positiveInteger(m.maxContextTokens),
        inputPrice: displayToStored(m.inputPrice, priceUnit),
        outputPrice: displayToStored(m.outputPrice, priceUnit),
        inputCredits: parseFloat(m.inputCredits) || 0,
        outputCredits: parseFloat(m.outputCredits) || 0,
        extraConfig: m.extraConfig.trim() || undefined,
        disabled: m.disabled,
      })));

      if (editingProvider) {
        await updateLLMProvider(editingProvider.id, {
          name: providerForm.name.trim(),
          apiBase: providerForm.apiBase.trim(),
          ...(providerForm.apiKey ? { apiKey: providerForm.apiKey } : {}),
          models: modelsJson,
          maxConcurrency: providerForm.maxConcurrency.trim() === '' ? null : Number(providerForm.maxConcurrency),
        });
      } else {
        await createLLMProvider({
          name: providerForm.name.trim(),
          apiBase: providerForm.apiBase.trim(),
          apiKey: providerForm.apiKey,
          models: modelsJson,
          maxConcurrency: providerForm.maxConcurrency.trim() === '' ? null : Number(providerForm.maxConcurrency),
        });
      }
      setShowProviderModal(false);
      await load();
    } catch (err: any) {
      setProviderError(err.message || t('llm.saveFailed'));
    } finally {
      setProviderSaving(false);
    }
  };

  const handleToggleProvider = async (p: LLMProvider) => {
    try {
      await toggleLLMProvider(p.id, !p.disabled);
      await load();
    } catch (err: any) {
      toast.error(err.message || t('llm.operationFailed'));
    }
  };

  const handleDeleteProvider = async (p: LLMProvider) => {
    try {
      await deleteLLMProvider(p.id);
      setDeletingProvider(null);
      await load();
    } catch (err: any) {
      toast.error(err.message || t('llm.deleteFailed'));
    }
  };

  // Role config
  const handleRoleChange = (role: keyof LLMRoleConfigMap, field: 'providerId' | 'model', value: string) => {
    setRoleConfig(prev => {
      const updated = { ...prev, [role]: { ...prev[role], [field]: value } };
      // Reset model when provider changes
      if (field === 'providerId') {
        updated[role] = { providerId: value, model: '' };
      }
      return updated;
    });
  };

  const getProviderModels = (providerId: string): LLMModelInfo[] => {
    const p = providers.find(p => p.id === providerId);
    return p ? parseModels(p.models) : [];
  };

  const handleSaveRoles = async () => {
    setSavingRoles(true);
    setRoleMsg('');
    try {
      await updateLLMRoleConfig(roleConfig);
      setRoleMsg(t('llm.saveSuccess'));
      setTimeout(() => setRoleMsg(''), 2000);
    } catch (err: any) {
      setRoleMsg(err.message || t('llm.saveFailed'));
    } finally {
      setSavingRoles(false);
    }
  };

  const addModel = () => setModelList(prev => [...prev, emptyModel()]);
  const removeModel = (idx: number) => setModelList(prev => prev.filter((_, i) => i !== idx));
  const updateModel = (idx: number, field: keyof ModelFormItem, value: any) => {
    setModelList(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 2rem', textAlign: 'center', color: 'var(--text-3)' }}>
        {t('llm.loading')}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 2rem' }}>
      <Breadcrumb items={[{ label: t('llm.breadcrumbHome'), to: '/projects' }, { label: t('llm.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('llm.pageTitle')}</h1>
      </div>

      {/* Section 1: Provider Management */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{t('llm.providerManagement')}</h2>
          <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }} onClick={openCreateProvider}>
            {t('llm.createProvider')}
          </button>
        </div>

        {providers.length === 0 ? (
          <p style={{ color: 'var(--text-3)', fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>
            {t('llm.noProviders')}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {providers.map(p => {
              const models = parseModels(p.models);
              return (
                <div key={p.id} className="list-item" style={{ alignItems: 'flex-start', opacity: p.disabled ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text)' }}>{p.name}</span>
                      {p.disabled && <span style={{ fontSize: '0.7rem', color: 'var(--danger)', background: '#fff0f0', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>{t('llm.disabled')}</span>}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '0.3rem' }}>
                      API Base: <span style={{ color: 'var(--text-2)' }}>{p.apiBase}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '0.5rem' }}>
                      API Key: <span style={{ fontFamily: 'monospace' }}>{p.apiKeyMasked}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '0.5rem' }}>
                      并发上限: <span style={{ color: 'var(--text-2)' }}>{p.maxConcurrency == null ? '默认' : (p.maxConcurrency === 0 ? '不限' : p.maxConcurrency)}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {models.map((m, i) => (
                        <span key={i} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.2rem 0.5rem', background: m.disabled ? '#f5f5f5' : 'var(--surface-2)', borderRadius: '4px',
                          fontSize: '0.75rem', color: m.disabled ? 'var(--text-3)' : 'var(--text-2)',
                          textDecoration: m.disabled ? 'line-through' : 'none',
                        }}>
                          {m.displayName || m.name}
                          {m.supportThinking && <span title={t('llm.thinkingTooltip')} style={{ color: '#8b5cf6' }}>🧠</span>}
                          {m.supportVision && <span title={t('llm.visionTooltip')} style={{ color: '#06b6d4' }}>👁</span>}
                          {m.fixedParams && <span title={t('llm.fixedParamsTooltip')} style={{ color: '#f59e0b' }}>⚡</span>}
                          {m.extraConfig && <span title={t('llm.extraConfigTooltip')} style={{ color: '#10b981', fontWeight: 600 }}>{'{}'}</span>}
                          {m.maxOutputTokens ? <span title={t('llm.maxOutputTokensTooltip')} style={{ color: 'var(--text-3)', fontSize: '0.65rem' }}>{m.maxOutputTokens >= 1000 ? `${Math.round(m.maxOutputTokens / 1024)}K` : m.maxOutputTokens}</span> : null}
                          {(m.inputPrice || m.outputPrice) ? <span style={{ color: 'var(--text-3)' }}>¥{m.inputPrice || 0}/{m.outputPrice || 0}/kT</span> : null}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="list-item-actions" style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem', flexShrink: 0, alignItems: 'center' }}>
                    <button
                      className={p.disabled ? 'btn btn-primary' : 'btn btn-ghost'}
                      style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                      onClick={() => handleToggleProvider(p)}
                    >
                      {p.disabled ? t('llm.enable') : t('llm.disable')}
                    </button>
                    <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                      onClick={() => openEditProvider(p)}>{t('llm.edit')}</button>
                    <button className="btn btn-danger" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                      onClick={() => setDeletingProvider(p)}>{t('llm.delete')}</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section 2: Role Binding */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 600 }}>{t('llm.roleBinding')}</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
          {t('llm.roleBindingDesc')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {ROLE_KEYS.map(({ key, labelKey, descKey }) => {
            const rc = roleConfig[key];
            const models = getProviderModels(rc.providerId);
            return (
              <div key={key} data-role-key={key} style={{
                display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: '0.75rem', alignItems: 'center',
                padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text)' }}>{t(labelKey)}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{t(descKey)}</div>
                </div>
                <div>
                  <label className="form-label">{t('llm.provider')}</label>
                  <select className="input" style={{ fontSize: '0.8rem', padding: '0.4rem' }}
                    value={rc.providerId}
                    onChange={e => handleRoleChange(key, 'providerId', e.target.value)}>
                    <option value="">{t('llm.useDefault')}</option>
                    {providers.filter(p => !p.disabled).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    {/* 当前绑定的供应商已禁用时仍显示（标记警告） */}
                    {rc.providerId && providers.find(p => p.id === rc.providerId)?.disabled && (
                      <option value={rc.providerId} style={{ color: 'red' }}>
                        {providers.find(p => p.id === rc.providerId)?.name} ({t('llm.disabled')})
                      </option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="form-label">{t('llm.model')}</label>
                  <select className="input" style={{ fontSize: '0.8rem', padding: '0.4rem' }}
                    value={rc.model}
                    onChange={e => handleRoleChange(key, 'model', e.target.value)}
                    disabled={!rc.providerId}>
                    <option value="">{t('llm.selectModel')}</option>
                    {models.filter(m => !m.disabled).map(m => (
                      <option key={m.name} value={m.name}>
                        {m.displayName || m.name}
                        {m.supportThinking ? ' 🧠' : ''}
                        {m.supportVision ? ' 👁' : ''}
                        {m.fixedParams ? ' ⚡' : ''}
                      </option>
                    ))}
                    {/* 当前绑定的模型已禁用时仍显示（标记警告） */}
                    {rc.model && models.find(m => m.name === rc.model)?.disabled && (
                      <option value={rc.model}>
                        {models.find(m => m.name === rc.model)?.displayName || rc.model} ({t('llm.disabled')})
                      </option>
                    )}
                  </select>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          {roleMsg && <span style={{ fontSize: '0.8rem', color: roleMsg === t('llm.saveSuccess') ? 'var(--success)' : 'var(--danger)' }}>{roleMsg}</span>}
          <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} onClick={handleSaveRoles} disabled={savingRoles}>
            {savingRoles ? t('llm.saving') : t('llm.saveRoles')}
          </button>
        </div>
      </div>

      {/* Provider Modal */}
      <Modal
        open={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        title={editingProvider ? t('llm.editProvider') : t('llm.createProviderTitle')}
        width="560px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowProviderModal(false)}>{t('llm.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSaveProvider} disabled={providerSaving}>
              {providerSaving ? t('llm.saving') : t('llm.save')}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('llm.providerName')}</label>
          <input className="input" value={providerForm.name}
            onChange={e => setProviderForm(p => ({ ...p, name: e.target.value }))}
            placeholder={t('llm.providerNamePlaceholder')} />
        </div>

        <div className="form-group">
          <label className="form-label">{t('llm.apiBase')}</label>
          <input className="input" value={providerForm.apiBase}
            onChange={e => setProviderForm(p => ({ ...p, apiBase: e.target.value }))}
            placeholder={t('llm.apiBasePlaceholder')} />
        </div>

        <div className="form-group">
          <label className="form-label">
            {t('llm.apiKey')}
            {editingProvider && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>{t('llm.apiKeyKeepHint', { masked: editingProvider.apiKeyMasked })}</span>}
          </label>
          <input className="input" type="password" value={providerForm.apiKey}
            onChange={e => setProviderForm(p => ({ ...p, apiKey: e.target.value }))}
            placeholder={editingProvider ? t('llm.apiKeyPlaceholderEdit') : t('llm.apiKeyPlaceholderNew')} />
        </div>

        <div className="form-group">
          <label className="form-label">{t('llm.maxConcurrency', { defaultValue: '并发上限' })}</label>
          <input className="input" type="number" min={0} value={providerForm.maxConcurrency}
            onChange={e => setProviderForm(p => ({ ...p, maxConcurrency: e.target.value }))}
            placeholder="并发上限(空=默认, 0=不限)" />
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>{t('llm.modelList')}</label>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('llm.priceUnitLabel')}</span>
              <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                {(['kToken', 'MToken'] as const).map(u => (
                  <button key={u} type="button"
                    style={{
                      padding: '0.15rem 0.5rem', fontSize: '0.7rem', border: 'none', cursor: 'pointer',
                      background: priceUnit === u ? 'var(--primary)' : 'var(--surface-2)',
                      color: priceUnit === u ? '#fff' : 'var(--text-2)',
                    }}
                    onClick={() => {
                      if (u === priceUnit) return;
                      // 切换单位时换算已填的价格
                      const factor = u === 'MToken' ? 1000 : 0.001;
                      setModelList(prev => prev.map(m => ({
                        ...m,
                        inputPrice: m.inputPrice ? String(+(parseFloat(m.inputPrice) * factor).toPrecision(10)) : '',
                        outputPrice: m.outputPrice ? String(+(parseFloat(m.outputPrice) * factor).toPrecision(10)) : '',
                      })));
                      setPriceUnit(u);
                    }}
                  >
                    {u === 'kToken' ? t('llm.pricePerKToken') : t('llm.pricePerMToken')}
                  </button>
                ))}
              </div>
            </div>
            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
              onClick={addModel}>{t('llm.addModel')}</button>
          </div>

          {modelList.map((m, i) => (
            <div key={i} style={{
              border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{t('llm.modelId')}</label>
                  <input className="input" style={{ fontSize: '0.8rem' }} value={m.name}
                    onChange={e => updateModel(i, 'name', e.target.value)}
                    placeholder={t('llm.modelIdPlaceholder')} />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{t('llm.displayName')}</label>
                  <input className="input" style={{ fontSize: '0.8rem' }} value={m.displayName}
                    onChange={e => updateModel(i, 'displayName', e.target.value)}
                    placeholder={t('llm.displayNamePlaceholder')} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  {modelList.length > 1 && (
                    <button className="btn btn-danger" style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                      onClick={() => removeModel(i)}>{t('llm.delete')}</button>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={m.supportThinking}
                    onChange={e => updateModel(i, 'supportThinking', e.target.checked)} />
                  {t('llm.supportThinking')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={m.supportVision}
                    onChange={e => updateModel(i, 'supportVision', e.target.checked)} />
                  {t('llm.supportVision')}
                </label>
                {m.supportVision && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={m.useOverseasBucket}
                      onChange={e => updateModel(i, 'useOverseasBucket', e.target.checked)} />
                    {t('llm.useOverseasBucket')}
                  </label>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={m.fixedParams}
                    onChange={e => updateModel(i, 'fixedParams', e.target.checked)} />
                  {t('llm.fixedParams')}
                  <span className="help-tip" onClick={e => e.preventDefault()}>?
                    <span className="help-tip__bubble">{t('llm.fixedParamsTooltip')}</span>
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: m.disabled ? 'var(--danger)' : 'var(--text-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={m.disabled}
                    onChange={e => updateModel(i, 'disabled', e.target.checked)} />
                  {t('llm.disableModel')}
                </label>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{t('llm.maxOutputTokens')}</label>
                  <input className="input" type="number" min="0" step="1" style={{ fontSize: '0.8rem' }}
                    value={m.maxOutputTokens} onChange={e => updateModel(i, 'maxOutputTokens', e.target.value)} placeholder={t('llm.maxOutputTokensPlaceholder')} />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{t('llm.maxContextTokens')}</label>
                  <input className="input" type="number" min="1" step="1" style={{ fontSize: '0.8rem' }}
                    aria-label={t('llm.maxContextTokens')}
                    value={m.maxContextTokens} onChange={e => updateModel(i, 'maxContextTokens', e.target.value)} placeholder={t('llm.maxContextTokensPlaceholder')} />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{t('llm.inputPrice')}</label>
                  <input className="input" type="number" min="0" step="any" style={{ fontSize: '0.8rem' }}
                    value={m.inputPrice} onChange={e => updateModel(i, 'inputPrice', e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{t('llm.outputPrice')}</label>
                  <input className="input" type="number" min="0" step="any" style={{ fontSize: '0.8rem' }}
                    value={m.outputPrice} onChange={e => updateModel(i, 'outputPrice', e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem', color: 'var(--warning)' }}>{t('llm.inputCredits')}</label>
                  <input className="input" type="number" min="0" step="any" style={{ fontSize: '0.8rem' }}
                    value={m.inputCredits} onChange={e => updateModel(i, 'inputCredits', e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.7rem', color: 'var(--warning)' }}>{t('llm.outputCredits')}</label>
                  <input className="input" type="number" min="0" step="any" style={{ fontSize: '0.8rem' }}
                    value={m.outputCredits} onChange={e => updateModel(i, 'outputCredits', e.target.value)} placeholder="0" />
                </div>
              </div>
              <div style={{ marginTop: '0.5rem' }}>
                <label className="form-label" style={{ fontSize: '0.7rem' }}>
                  {t('llm.extraConfig')}
                  <span className="help-tip" onClick={e => e.preventDefault()}>?
                    <span className="help-tip__bubble">{t('llm.extraConfigTooltip')}</span>
                  </span>
                </label>
                <textarea className="input" rows={3}
                  style={{ fontSize: '0.75rem', fontFamily: 'monospace', resize: 'vertical' }}
                  value={m.extraConfig}
                  onChange={e => updateModel(i, 'extraConfig', e.target.value)}
                  placeholder='{"json_extra_body": {"response_format": {"type": "json_object"}}}' />
              </div>
            </div>
          ))}
        </div>

        {providerError && <p className="error-text">{providerError}</p>}
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deletingProvider}
        onCancel={() => setDeletingProvider(null)}
        onConfirm={() => deletingProvider && handleDeleteProvider(deletingProvider)}
        title={t('llm.confirmDeleteTitle')}
        message={t('llm.confirmDeleteMessage', { name: deletingProvider?.name })}
        confirmText={t('llm.confirmDeleteText')}
        confirmDanger
      />
    </div>
  );
};

export default LLMConfig;
